import { type UnifiedJobSearchRequest, type UnifiedJobSearchResponse } from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';

import { ROWS_PER_PAGE } from '../../../../constants';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { EventClientService } from '../../../../tools/helpers';
import { useSearchPages } from '../useSearchPages';
import { type UnifiedJobSearchScheme } from './utils';

/**
 * Format a Date as YYYY-MM-DD in local timezone (not UTC)
 * This prevents the "selected date - 1" bug caused by timezone conversion
 * We manually construct the date string using local date components to avoid UTC conversion
 */
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface UnifiedJobSearchFilters {
  filters: UnifiedJobSearchScheme;
  page: number;
  pageSize?: number;
  orderBy?: string;
  orderDir?: 'ASC' | 'DESC';
}

/**
 * Convert form values to API request format
 */
const buildUnifiedJobSearchRequest = (
  filters: UnifiedJobSearchScheme,
  page: number,
  pageSize = 25,
  orderBy = 'date_started',
  orderDir: 'ASC' | 'DESC' = 'DESC',
): UnifiedJobSearchRequest => {
  return {
    // Primary search
    jobNumber: filters.jobNumber || '',
    departmentId: filters.departmentId || 0,

    // Customer filters
    customerName: filters.customerName || '',
    businessName: filters.businessName || '',
    firstName: filters.firstName || '',
    lastName: filters.lastName || '',

    // Property filters
    propertyId: filters.propertyId || 0,
    propertyAddress: filters.propertyAddress || '',
    propertyAdditionalAddressLine: '',
    city: filters.city || '',
    state: filters.state || '',
    zip: filters.zip || '',

    // Job status filters
    jobStatus: filters.jobStatus || '',
    paymentStatus: filters.paymentStatus || '',

    // Date filters - use formatDateLocal to prevent timezone conversion issues
    dateStartedFrom: filters.dateStartedFrom ? formatDateLocal(filters.dateStartedFrom) : '',
    dateStartedTo: filters.dateStartedTo ? formatDateLocal(filters.dateStartedTo) : '',
    dateEndedFrom: filters.dateEndedFrom ? formatDateLocal(filters.dateEndedFrom) : '',
    dateEndedTo: filters.dateEndedTo ? formatDateLocal(filters.dateEndedTo) : '',

    // Job search has no completion-date field on its form. Sent empty so this
    // keeps its current behaviour; the Pending Billing queue is the caller that
    // uses the range.
    dateCompletedFrom: '',
    dateCompletedTo: '',

    // Additional filters
    technicianAssigned: filters.technicianAssigned || '',
    poNumber: filters.poNumber || '',
    isActive: filters.isActive ?? false,

    // Type filters
    projectsOnly: filters.projectsOnly ?? false,
    serviceCallsOnly: filters.serviceCallsOnly ?? false,

    // New filters
    name: filters.name || '',
    logNotes: filters.logNotes || '',
    isResidential: filters.isResidential || 0,
    jobTypeId: filters.jobTypeId || 0,
    jobSubtypeId: filters.jobSubtypeId || 0,
    servicesNeeded: filters.servicesNeeded || '',
    globalSearch: filters.globalSearch || '',

    // Pagination
    pageNumber: page,
    pageSize,

    // Sorting
    orderBy,
    orderDir,

    // Special flags
    countOnly: false,
    withoutLimit: false,

    // Not a search filter. The Pending Billing queue sets this; general job
    // search never does, and expressing it as jobStatus/paymentStatus would not
    // be equivalent because those also match a project's sub-job aggregates.
    pendingBillingQueue: false,

    // Geo-filtering (use 0 when not searching by location)
    geoLat: 0,
    geoLng: 0,
    radiusMiles: 0,
  };
};

/**
 * React Query hook for unified job search
 */
export const useUnifiedJobSearchQuery = <TSelectedData = UnifiedJobSearchResponse,>({
  filters,
  page,
  pageSize = 25,
  orderBy = 'date_started',
  orderDir = 'DESC',
  enabled = true,
  select,
  useV2 = false,
}: UnifiedJobSearchFilters & {
  enabled?: boolean;
  select?: (data: UnifiedJobSearchResponse) => TSelectedData;
  useV2?: boolean;
}) => {
  const request = buildUnifiedJobSearchRequest(filters, page, pageSize, orderBy, orderDir);

  return useQuery({
    queryKey: [queryKeys.events.root, 'unified-search', useV2, request],
    queryFn: async () => {
      return useV2
        ? await EventClientService.SearchJobsV2(request)
        : await EventClientService.SearchJobs(request);
    },
    enabled,
    select,
    // Keep previous data while fetching new data (smooth pagination)
    // Cache for 1 minute
    staleTime: 1000 * 60,
  });
};

/**
 * Hook for exporting all results without pagination
 */
export const useUnifiedJobSearchExportQuery = ({
  filters,
  orderBy = 'date_started',
  orderDir = 'DESC',
  enabled = false,
  useV2 = false,
}: Omit<UnifiedJobSearchFilters, 'page' | 'pageSize'> & {
  enabled?: boolean;
  useV2?: boolean;
}) => {
  const request: UnifiedJobSearchRequest = {
    ...buildUnifiedJobSearchRequest(filters, 0, 25, orderBy, orderDir),
    withoutLimit: true,
    pageNumber: 0,
  };

  return useQuery({
    queryKey: [queryKeys.events.root, 'unified-search-export', useV2, request],
    queryFn: async () => {
      return useV2
        ? await EventClientService.SearchJobsV2(request)
        : await EventClientService.SearchJobs(request);
    },
    enabled,
    // Don't cache export queries
    staleTime: 0,
  });
};

/**
 * Job search on phones: the same request at the same page size, loaded page after page as the
 * list scrolls. Under the events root like the paged query, so anything that refreshes job
 * lists refreshes these pages too.
 */
export const useUnifiedJobSearchPagesQuery = ({
  filters,
  orderBy = 'date_started',
  orderDir = 'DESC',
  enabled = true,
}: Omit<UnifiedJobSearchFilters, 'page' | 'pageSize'> & { enabled?: boolean }) => {
  const firstPage = buildUnifiedJobSearchRequest(filters, 0, ROWS_PER_PAGE, orderBy, orderDir);
  return useSearchPages({
    queryKey: [queryKeys.events.root, 'unified-search-pages', true, firstPage],
    fetchPage: (pageNumber) => EventClientService.SearchJobsV2({ ...firstPage, pageNumber }),
    enabled,
  });
};
